import React, { useCallback, useRef } from "react";

import { isAfter } from "date-fns";
import DataGrid, {
  Column,
  DataGridRef,
  Editing,
  FilterRow,
  Form,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel,
  Paging,
  Selection,
  Sorting,
  Toolbar,
} from "devextreme-react/data-grid";
import { EmptyItem, RequiredRule, SimpleItem } from "devextreme-react/form";
import ValidationGroup, {
  ValidationGroupRef,
} from "devextreme-react/validation-group";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import "devextreme/ui/date_box";
import "devextreme/ui/select_box";
import "devextreme/ui/text_area";
import { run as eRun, sleep } from "effection";
import { Operation } from "starfx";

import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import { TFiscalBook } from "@app/statefx/schema/fiscalBooks.def";
import { select_apiProf_issuersArray } from "@app/statefx/selectors/invoiceIssuers.sel";
import { selectfiscalBooks } from "@app/statefx/selectors/uiFiscalBooks.sel";
import {
  sfxDeleteSerialNumber,
  sfxInsertOrUpdateSerialNumber,
} from "@app/statefx/xThunks/ds-fiscalBooks";
import { setCurrenFiscalBookId } from "@app/statefx/xThunks/uiFiscalBooks";

import "./serials.scss";

import type { FormTypes } from "devextreme-react/form";
/* 
export type TFiscalBook = {
  FCT_FF: number;
  PREFIX: string;
  NUMARCIFRE: number;
  SUFIX: string;
  NUMAR: number;
  EMITENT: string;
  CIF_EMITENT: string;
  CHITANTA: number;
  CHELTUIELI: number;
  ESTE_CURENT: number;
  ALIAS: string;
  EXPLICATII: string;
  ALIAS_BIFAT: number;
  ACTIV_DELA: string;
  INACTIV_DELA: string;
  AMA_ID: number;
  NOTA: string;
  ERROR_MESSAGE: string;

};
 */
const hiddenField = {
  visible: false,
  showInColumnChooser: false,
  allowEditing: false,
};

export const FiscalBooks = () => {
  const serialsRef = useRef<DataGridRef>(null);
  const serialValidatorRef = useRef<ValidationGroupRef>(null);
  const dispatch = useFxDispatch();
  const serials = useFxSelector(selectfiscalBooks);
  const issuers = useFxSelector(select_apiProf_issuersArray);
  const selectedFBk_ = useFxSelector(schema.uiFiscalBooks.select);
  const selectedBookId = selectedFBk_.selectedBookId;
  const dataSource = new DataSource({
    store: new CustomStore({
      loadMode: "raw",
      key: "FCT_FF",
      load: () => {
        return serials as (TFiscalBook & { DENUMIRE_EMITENT: string })[];
      },
      insert: async (values) => {
        // todo: https://github.com/VldMrgnn/jk-react-app/issues/93 validate the duplication of the serial itself (prefix+ [range] + sufix)
        dispatch(sfxInsertOrUpdateSerialNumber(values));
        return Promise.resolve(values);
      },
      update: async (key, values) => {
        const data0 = serials.find((p) => p.FCT_FF === key);
        if (data0 === undefined) {
          dispatch(sfxInsertOrUpdateSerialNumber(values));
        } else {
          return dispatch(
            sfxInsertOrUpdateSerialNumber({ ...data0, ...values })
          );
        }
        return Promise.resolve(values);
      },
      remove: (key) => {
        dispatch(sfxDeleteSerialNumber({ FCT_FF: key }));
        return Promise.resolve(key);
      },
    }),
  });

  const newRowHandler = useCallback((e) => {
    e.data.ACTIV_DELA = new Date().toISOString().split("T")[0];
    e.data.ALIAS = "";
    e.data.ALIAS_BIFAT = 0;
    e.data.AMA_ID = 0;
    e.data.CHELTUIELI = 0;
    e.data.CHITANTA = 0;
    e.data.CIF_EMITENT = issuers[0].CIF;
    e.data.CIF_EMITENT_hidden = issuers[0].CIF;
    e.data.DENUMIRE_EMITENT = issuers[0].DENUMIRE;
    e.data.EMITENT = issuers[0].ABREVIERE;
    e.data.ERROR_MESSAGE = "";
    e.data.ESTE_CURENT = 0;
    e.data.EXEMPLU = "";
    e.data.EXPLICATII = "";
    e.data.FCT_FF = -1;
    e.data.INACTIV_DELA = null;
    e.data.NOTA = "";
    e.data.NUMAR = 0;
    e.data.NUMARCIFRE = 4;
    e.data.PREFIX = issuers[0].ABREVIERE;
    e.data.SUFIX = "";
    e.data.UMPLE_ZEROURI = 0;
    e.data as TFiscalBook;
    eRun(function* (): Operation<void> {
      yield* sleep(300);
      const dox = document.getElementById("xonx-2332");
      if (dox) {
        dox.innerHTML = `${e.data.PREFIX || ""}${`${
          (e.data.NUMAR || 0) + 1
        }`.padStart(e.data.NUMARCIFRE, "0")}${e.data.SUFIX || ""}`;
      }
    });
  }, []);

  const editorPreparing = (e) => {
    if (e.parentType === "dataRow" && e.row.data?.FCT_FF > 0) {
      if (["INACTIV_DELA", "NOTA", "NUMAR"].includes(e.dataField)) {
        e.editorOptions.disabled = false;
      } else {
        e.editorOptions.disabled = true;
      }
      void 0;
    } else {
      void 0;
    }
  };

  const onRowValidating = (e: any) => {
    if (e.brokenRules?.length) {
      e.isValid = false;
      e.errorText = e.brokenRules.map((x: any) => x.message).join("\n");
      console.log("Validation Error 1:", e);
      return;
    }
    // manual validation
    const newFullRow = { ...e.oldData, ...e.newData };
    if (newFullRow.ACTIV_DELA && newFullRow.INACTIV_DELA) {
      if (
        isAfter(
          new Date(newFullRow.ACTIV_DELA),
          new Date(newFullRow.INACTIV_DELA)
        )
      ) {
        e.errorText =
          "Data de activare trebuie sa fie mai mica sau egala cu data de inactivare";
        e.isValid = false;
        return;
      }
    }
    e.isValid = true;
    return;
  };

  const editingStart = useCallback((e: any) => {
    // const currentIssurer = issuers.find((p) => p.CIF === e.data.CIF_EMITENT);
    // serialsRef.current!.instance().option('editing.allowDeleting', e.data.ELEMENT_COUNT === 0);
    // console.log('e.data.ELEMENT_COUNT', e.data.ELEMENT_COUNT)
    // console.log('currentIssurer', currentIssurer)
    e.data.CIF_EMITENT_hidden = e.data.CIF_EMITENT;
    eRun(function* (): Operation<void> {
      yield* sleep(300);
      const dox = document.getElementById("xonx-2332");
      if (dox) {
        dox.innerHTML = `${e.data.PREFIX || ""}${`${
          (e.data.NUMAR || 0) + 1
        }`.padStart(e.data.NUMARCIFRE, "0")}${e.data.SUFIX || ""}`;
      }
    });
  }, []);

  const noItemsOnThisBook = (e) =>
    e.row.data.ELEMENT_COUNT === 0 ? true : false;

  function numarSetCellValue(this, newData, value, currentRowData) {
    Object.assign(newData, currentRowData);
    newData[this.dataField] = value;
    // const dataGridInstance = serialsRef.current!.instance;
    // const editing = dataGridInstance.option('editing');
    // const rowIndex = dataGridInstance.getRowIndexByKey(editing?.editRowKey!);
    // const a  = dataGridInstance.cellValue(rowIndex, 'PREFIX');
    // const b  = dataGridInstance.cellValue(rowIndex, 'NUMAR');
    // const c  = dataGridInstance.cellValue(rowIndex, 'NUMARCIFRE');
    // const d  = dataGridInstance.cellValue(rowIndex, 'SUFIX');
    const a = newData.PREFIX;
    const b = newData.NUMAR;
    const c = newData.NUMARCIFRE;
    const d = newData.SUFIX;
    const exampleValue = ` ${a || ""}${`${b + 1 || 0}`.padStart(+c, "0")}${
      d || ""
    }`;
    const dox = document.getElementById("xonx-2332");
    if (dox) {
      dox.innerHTML = exampleValue;
    }
    newData.EXEMPLU = exampleValue;
    this.defaultSetCellValue!(newData, value, currentRowData);
  }

  function setCifForEmitent(this, newData, value, currentRowData) {
    newData.CIF_EMITENT = value;
    newData.CIF_EMITENT_hidden = value;
    newData.DENUMIRE_EMITENT = issuers.find((p) => p.CIF === value)?.DENUMIRE;
    this.defaultSetCellValue!(newData, value, currentRowData);
  }

  const onSaving = async (e) => {
    const result = await serialValidatorRef.current!.instance().validate();
    e.cancel = !result.isValid;
  };
  const selectionChanged = (e) => {
    dispatch(setCurrenFiscalBookId({ selectedBookId: e.selectedRowKeys[0] }));
  };

  const onRowPrepared = (e) => {
    if (e.rowType === "data" && e.data?.isActive === false) {
      e.rowElement.style.color = "#b0b0b0";
    }
  };
  return (
    <ValidationGroup id="serials" ref={serialValidatorRef}>
      <DataGrid
        id={"dataGridSerials"}
        ref={serialsRef}
        dataSource={dataSource}
        showBorders={true}
        showRowLines={true}
        showColumnLines={true}
        columnAutoWidth={false}
        allowColumnReordering={true}
        allowColumnResizing={true}
        autoNavigateToFocusedRow={true}
        focusedRowEnabled={false}
        filterSyncEnabled={true}
        repaintChangesOnly={true}
        renderAsync={true}
        wordWrapEnabled={true}
        noDataText={""}
        selectedRowKeys={[selectedBookId]}
        onEditorPreparing={editorPreparing}
        showColumnHeaders={true}
        onInitNewRow={newRowHandler}
        onEditingStart={editingStart}
        onSelectionChanged={selectionChanged}
        // onCellPrepared={cellPrepared}
        onRowPrepared={onRowPrepared}
        onRowValidating={onRowValidating}
        onSaving={onSaving}
      >
        <Editing
          mode={"form"}
          allowUpdating={true}
          allowDeleting={noItemsOnThisBook}
          allowAdding={true}
          useIcons={true}
        >
          <Form labelMode="floating" labelLocation="top" colCount={6}>
            <SimpleItem
              dataField="CIF_EMITENT"
              name="CIF_EMITENT"
              colSpan={4}
              editorType={"dxSelectBox"}
              editorOptions={{
                items: issuers,
                displayExpr: "DENUMIRE",
                valueExpr: "CIF",
              }}
              label={{ text: "Emitentul facturii" }}
            >
              <RequiredRule message="CIF Emitent este obligatoriu" />
            </SimpleItem>
            <SimpleItem
              dataField="CIF_EMITENT"
              name="CIF_EMITENT_hidden"
              colSpan={2}
              editorOptions={{ disabled: true }}
              label={{ text: "CIF Facturier" }}
            />
            <SimpleItem dataField="PREFIX" visible={true} colSpan={2} />
            <SimpleItem
              dataField="NUMARCIFRE"
              visible={true}
              colSpan={2}
              editorType="dxNumberBox"
              editorOptions={{
                showSpinButtons: true,
                useLargeSpinButtons: true,
                min: 1,
                max: 5,
              }}
              label={{ text: "Nr.Cifre" }}
            >
              <RequiredRule message="Numar cifre este obligatoriu" />
            </SimpleItem>

            <SimpleItem dataField="SUFIX" visible={true} colSpan={2} />
            <EmptyItem colSpan={2} />
            <SimpleItem
              dataField="NUMAR"
              visible={true}
              colSpan={2}
              editorType="dxNumberBox"
              editorOptions={{
                showSpinButtons: true,
                useLargeSpinButtons: true,
                min: 0,
              }}
              label={{ text: "Nr. factura" }}
            >
              <RequiredRule message="Numar factura este obligatoriu" />
            </SimpleItem>

            <EmptyItem colSpan={2} />
            <EmptyItem colSpan={1} />
            <SimpleItem
              dataField="EXEMPLU"
              name="NUMARCIFRE2"
              label={{ text: " " }}
              editorType="dxTextArea"
              colSpan={4}
              editorOptions={{
                style: {
                  height: "3rem",
                  textAllign: "center",
                  paddingTop: "1rem",
                },
                elementAttr: { id: "xonx-2332" },
                stylingMode: "contained",
                height: 40,
                placeholder: "Exemplu",
              }}
            />
            <EmptyItem colSpan={1} />
            <SimpleItem
              dataField="CHITANTA"
              visible={true}
              colSpan={6}
              editorType="dxRadioGroup"
              editorOptions={{
                valueExpr: "value",
                displayExpr: "text",
                items: [
                  { value: 0, text: "Facturier" },
                  { value: 1, text: "Chitantier" },
                ],
                layout: "horizontal",
              }}
            >
              <RequiredRule message="Tipul este obligatoriu" />
            </SimpleItem>

            <SimpleItem
              dataField="NOTA"
              colSpan={6}
              editorType="dxTextArea"
              label={{ text: "Nota" }}
            />
            <SimpleItem
              dataField="ACTIV_DELA"
              colSpan={3}
              label={{ text: "Data activarii (0:00)" }}
              editorType="dxDateBox"
              editorOptions={{
                pickerType: "calendar",
                type: "date",
                applyValueMode: "useButtons",
              }}
            >
              <RequiredRule message="Data de activare este obligatorie" />
            </SimpleItem>
            <SimpleItem
              dataField="INACTIV_DELA"
              colSpan={3}
              label={{ text: "Data inactivarii (ora 0:00)" }}
              editorType="dxDateBox"
            />
          </Form>
        </Editing>
        <Sorting mode={"none"} />
        <Selection mode={"single"} />
        <Grouping autoExpandAll={false} />
        <GroupPanel visible={false} />
        <HeaderFilter visible={false} />
        <FilterRow visible={false} />
        <LoadPanel enabled={false} />
        <Paging enabled={false} />
        <Toolbar>
          <Item location="after" name="addRowButton" />
          <Item location="after" name="revertButton" />
          <Item location="after" name="saveButton" />
        </Toolbar>
        <Column
          dataField="CIF_EMITENT"
          caption="CIF Emitent"
          visible={true}
          setCellValue={setCifForEmitent}
        />
        <Column
          dataField="FCT_FF"
          caption="Factura/Chitanta"
          visible={true}
          cellRender={(e) => {
            return (
              <div>
                <span>{e.data.PREFIX}</span>
                <span>
                  {`${e.data.NUMAR || 0}`.padStart(e.data.NUMARCIFRE, "0")}
                </span>
                <span>{e.data.SUFIX}</span>
              </div>
            );
          }}
        />

        <Column dataField="CIF_EMITENT_hidden" visible={false} />
        <Column dataField="ALIAS_BIFAT" visible={false} />
        <Column dataField="AMA_ID" visible={false} />
        <Column dataField="ERROR_MESSAGE" visible={false} />
        <Column dataField="ALIAS" visible={false} />
        <Column dataField="ESTE_CURENT" visible={false} />
        <Column dataField="CHELTUIELI" visible={false} />
        <Column
          dataField="NUMAR"
          visible={false}
          setCellValue={numarSetCellValue}
        />
        <Column
          dataField="SUFIX"
          visible={false}
          setCellValue={numarSetCellValue}
        />
        <Column
          dataField="NUMARCIFRE"
          visible={false}
          setCellValue={numarSetCellValue}
        />
        <Column
          dataField="PREFIX"
          visible={false}
          setCellValue={numarSetCellValue}
        />

        <Column dataField="NOTA" visible={false} />

        <Column
          dataField="CHITANTA"
          caption="Tip"
          dataType="number"
          visible={true}
          calculateDisplayValue={(e) => (e.CHITANTA === 0 ? "Fact." : "Chit.")}
          width={50}
        />

        <Column
          dataField="ACTIV_DELA"
          caption="Data activarii"
          dataType="date"
          visible={true}
        />
        <Column
          dataField="INACTIV_DELA"
          caption="Data inactivarii"
          dataType="date"
          visible={true}
        />
        <Column dataField="EXEMPLU" dataType="string" visible={false} />
      </DataGrid>
    </ValidationGroup>
  );
};

/*
Softwiz
Facturier  SWS1990  activ de la 01.01.2021

todo: https://github.com/VldMrgnn/jk-react-app/issues/90 generate next number by validating against existing invoices, not just adding 1 to the last number
todo: https://github.com/VldMrgnn/jk-react-app/issues/91 Create a general validation for the series of a book.
todo: https://github.com/VldMrgnn/jk-react-app/issues/92 In the case of a duplicate number, the system should not allow the user to save the record. 

*/
